/**
 * Minimal i18n helper.
 *
 * Supported locales: en, es, vi, th, zh, fr, fil, ja, hi, ar, bn, pt, ru, ur, id, de, sw, mr
 * Detection order:
 *  1. URL query `?lang=xx`
 *  2. `localStorage.lang`
 *  3. `navigator.language` first segment
 *  4. Fallback: `en`
 *
 * SSR:
 *  - `resolveLocaleFromHeaders(headers)` derives locale from `Accept-Language`
 *  - `initLocale(locale?)` sets the initial messages bundle once
 *  - `getSsrLocale()` returns the initialized locale for state serialization
 */

import en from './locales/en.json'
import es from './locales/es.json'
import vi from './locales/vi.json'
import th from './locales/th.json'
import zh from './locales/zh.json'
import fr from './locales/fr.json'
import fil from './locales/fil.json'
import ja from './locales/ja.json'
import hi from './locales/hi.json'
import ar from './locales/ar.json'
import bn from './locales/bn.json'
import pt from './locales/pt.json'
import ru from './locales/ru.json'
import ur from './locales/ur.json'
import id from './locales/id.json'
import de from './locales/de.json'
import sw from './locales/sw.json'
import mr from './locales/mr.json'

export type Locale = 'en' | 'es' | 'vi' | 'th' | 'zh' | 'fr' | 'fil' | 'ja' | 'hi' | 'ar' | 'bn' | 'pt' | 'ru' | 'ur' | 'id' | 'de' | 'sw' | 'mr'
export type TranslationMap = typeof en

const locales: Record<Locale, TranslationMap> = { en, es, vi, th, zh, fr, fil, ja, hi, ar, bn, pt, ru, ur, id, de, sw, mr }

export function detectClientLocale(): Locale {
  try {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      const qp = url.searchParams.get('lang')
      if (qp && /^[a-z]{2,3}$/i.test(qp)) {
        const l = qp.toLowerCase() as Locale
        if (locales[l]) return l
      }

      // Inherit the locale already chosen by SSR (set on <html lang="…">).
      // This must come before localStorage so a stale saved value cannot
      // override the current request's detected locale.
      const htmlLang = (document.documentElement.lang || '').slice(0, 2).toLowerCase() as Locale
      if (htmlLang && locales[htmlLang]) return htmlLang

      const ls = window.localStorage.getItem('lang')
      if (ls && /^[a-z]{2,3}$/i.test(ls)) {
        const l = ls.toLowerCase() as Locale
        if (locales[l]) return l
      }

      const nav = (navigator.language || 'en').slice(0, 2).toLowerCase() as Locale
      if (locales[nav]) return nav
    }
  } catch {
    // no-op: default to en
  }
  return 'en'
}

export function resolveLocaleFromHeaders(
  headers?: Headers | Record<string, string | string[] | undefined>,
): Locale {
  if (!headers) return 'en'
  try {
    const raw =
      typeof (headers as Headers).get === 'function'
        ? ((headers as Headers).get('accept-language') || (headers as Headers).get('Accept-Language'))
        : ((headers as Record<string, string | string[] | undefined>)['accept-language'] || (headers as Record<string, string | string[] | undefined>)['Accept-Language'])

    const accept = Array.isArray(raw) ? raw[0] : raw
    if (typeof accept === 'string' && accept.trim()) {
      const first = accept.split(',')[0].trim().split('-')[0].toLowerCase()
      if (first && /^[a-z]{2,3}$/.test(first) && locales[first as Locale]) {
        return first as Locale
      }
    }
  } catch {
    // no-op
  }
  return 'en'
}

let currentLocale: Locale = 'en'
let messages: TranslationMap = locales[currentLocale]
let initialized = false

export function initLocale(locale?: Locale): Locale {
  if (initialized) return currentLocale
  if (locale && locales[locale]) {
    currentLocale = locale
    messages = locales[locale]
  } else if (typeof window !== 'undefined') {
    currentLocale = detectClientLocale()
    messages = locales[currentLocale]
  }
  initialized = true
  return currentLocale
}

export function setLocale(locale: Locale) {
  if (!locales[locale]) return
  currentLocale = locale
  messages = locales[locale]
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('lang', locale)
    }
  } catch {
    // no-op
  }
}

export function getLocale(): Locale {
  return currentLocale
}

export function getSsrLocale(): Locale {
  return currentLocale
}

export function t(key: string, params?: Record<string, string | number>): string {
  const parts = key.split('.')
  let value: any = messages
  for (const part of parts) {
    if (value && typeof value === 'object' && part in value) {
      value = value[part]
    } else {
      value = null
      break
    }
  }
  if (typeof value !== 'string') {
    const enMessages = locales.en
    let fallback: any = enMessages
    for (const part of parts) {
      if (fallback && typeof fallback === 'object' && part in fallback) {
        fallback = fallback[part]
      } else {
        return key
      }
    }
    value = fallback
  }
  if (typeof value !== 'string') return key
  if (!params) return value
  return value.replace(/\{(\w+)\}/g, (_, k) => String(params[k] ?? `{${k}}`))
}

export { locales }
